import * as THREE from 'three';

/**
 * Shader-only point lights. A real THREE.PointLight is evaluated by every lit pixel of every lit material,
 * even at zero intensity, and the floor alone covers most of the screen. These two lights (the gate fire
 * and the pooled impact flash) are instead added only to the materials that visibly catch them — floor,
 * road, walls and the horde — as a diffuse term with the same inverse-square falloff and distance cutoff.
 *
 * Slot layout: uFakeLightPos[i] = (view-space x, y, z, distance), uFakeLightCol[i] = colour × intensity.
 */
export const FAKE_LIGHTS = 3;
export const fakeLightUniforms = {
  uFakeLightPos: { value: Array.from({ length: FAKE_LIGHTS }, () => new THREE.Vector4(0, -100, 0, 1)) },
  uFakeLightCol: { value: Array.from({ length: FAKE_LIGHTS }, () => new THREE.Vector3()) },
};

/** World positions; the uniforms carry them in view space (see syncFakeLights). */
const world = Array.from({ length: FAKE_LIGHTS }, () => new THREE.Vector3(0, -100, 0));

/** Set slot `i` (0 = gate fire, 1 = impact flash, 2 = summon seal). */
export function setFakeLight(i: number, pos: THREE.Vector3, distance: number, color: THREE.Color, intensity: number) {
  world[i].copy(pos);
  fakeLightUniforms.uFakeLightPos.value[i].w = Math.max(0.01, distance);
  fakeLightUniforms.uFakeLightCol.value[i].set(color.r * intensity, color.g * intensity, color.b * intensity);
}

const _v = new THREE.Vector3();
/**
 * Move the lights into the camera's view space once per frame, so no fragment has to: every lit pixel of the
 * floor, road, walls and horde used to multiply each light by the view matrix itself. Call before rendering.
 */
export function syncFakeLights(camera: THREE.Camera) {
  camera.updateMatrixWorld();
  for (let i = 0; i < FAKE_LIGHTS; i++) {
    _v.copy(world[i]).applyMatrix4(camera.matrixWorldInverse);
    const u = fakeLightUniforms.uFakeLightPos.value[i]; u.set(_v.x, _v.y, _v.z, u.w);
  }
}

const PARS = /* glsl */ `
  uniform vec4 uFakeLightPos[${FAKE_LIGHTS}];
  uniform vec3 uFakeLightCol[${FAKE_LIGHTS}];
`;
/** Diffuse contribution, added after the lighting (so material patches that scale the emissive never touch it). */
const APPLY = /* glsl */ `
  for (int i = 0; i < ${FAKE_LIGHTS}; i++) {
    vec3 c = uFakeLightCol[i];
    if (c.r + c.g + c.b < 1e-4) continue;
    vec3 lp = uFakeLightPos[i].xyz + vViewPosition;
    float d = length(lp);
    // three.js physical point-light falloff (decay 2) with its smooth range cutoff
    float cutoff = pow(clamp(1.0 - pow(d / uFakeLightPos[i].w, 4.0), 0.0, 1.0), 2.0);
    float atten = cutoff / max(d * d, 0.01);
    #ifdef STANDARD
      // ×2: stands in for the rough specular lobe a real light adds (measured 2.1× on the wall material)
      vec3 albedo = diffuseColor.rgb * (1.0 - metalnessFactor) * 2.0;
    #else
      vec3 albedo = diffuseColor.rgb;
    #endif
    totalEmissiveRadiance += albedo * c * atten * max(dot(normal, lp / max(d, 1e-4)), 0.0) * RECIPROCAL_PI;
  }
`;

/** Patch a lit material's compiled shader so it also receives the shader-only lights. */
export function withFakeLights(sh: { uniforms: Record<string, THREE.IUniform>; fragmentShader: string }) {
  Object.assign(sh.uniforms, fakeLightUniforms);
  sh.fragmentShader = sh.fragmentShader
    .replace('#include <common>', `#include <common>\n${PARS}`)
    .replace('#include <aomap_fragment>', `#include <aomap_fragment>\n${APPLY}`);
}

/** Wrap a material's onBeforeCompile (keeping any existing patch) and give it its own program key. */
export function addFakeLights(m: THREE.Material) {
  const prev = m.onBeforeCompile.bind(m), prevKey = m.customProgramCacheKey.bind(m);
  m.onBeforeCompile = (sh, r) => { prev(sh, r); withFakeLights(sh); };
  m.customProgramCacheKey = () => prevKey() + '+fakelights';
}
